import type { AnalyticsSession } from "@/lib/analyticsDB";

const TEAM_SIZE_LABELS: Record<string, string> = {
  "1-10": "1-10 people",
  "11-50": "11-50 people",
  "51-200": "51-200 people",
  "200+": "200+ people",
};

const WORK_MODE_LABELS: Record<string, string> = {
  office: "Office / On-site",
  remote: "Remote / Hybrid",
  field: "Field / Mobile",
};

function DistributionBar({
  label,
  count,
  total,
  color,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-silver-300 w-28 flex-shrink-0">{label}</span>
      <div className="flex-1 h-2 bg-graphite-700/60 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-medium text-white w-8 text-right">{count}</span>
      <span className="text-[10px] text-silver-500 w-8 text-right">{pct}%</span>
    </div>
  );
}

interface UserSegmentsProps {
  sessions: AnalyticsSession[];
}

export default function UserSegments({ sessions }: UserSegmentsProps) {
  const teamCounts = new Map<string, number>();
  const modeCounts = new Map<string, number>();

  for (const s of sessions) {
    const teamSize = s.answers?.teamSize;
    if (teamSize && typeof teamSize === "string") {
      teamCounts.set(teamSize, (teamCounts.get(teamSize) || 0) + 1);
    }
    const workMode = s.answers?.workMode;
    if (workMode && typeof workMode === "string") {
      modeCounts.set(workMode, (modeCounts.get(workMode) || 0) + 1);
    }
  }

  const teamData = Array.from(teamCounts.entries()).map(([key, count]) => ({
    key,
    label: TEAM_SIZE_LABELS[key] || key,
    count,
  }));

  const modeData = Array.from(modeCounts.entries()).map(([key, count]) => ({
    key,
    label: WORK_MODE_LABELS[key] || key,
    count,
  }));

  const teamTotal = teamData.reduce((s, d) => s + d.count, 0);
  const modeTotal = modeData.reduce((s, d) => s + d.count, 0);

  const COLORS = ["#10b981", "#059669", "#34d399", "#14b8a6", "#0ea5e9", "#8b5cf6"];

  return (
    <div className="glass-card p-4 md:p-5">
      <div className="mb-5">
        <h3 className="text-base font-bold text-white">User Segments</h3>
        <p className="text-xs text-silver-400 mt-0.5">Team size and work mode distribution</p>
      </div>

      <div className="space-y-6">
        {/* Team Size */}
        <div>
          <h4 className="text-xs font-semibold text-silver-300 uppercase tracking-wider mb-3">Team Size</h4>
          {teamTotal === 0 ? (
            <p className="text-sm text-silver-400 py-4 text-center">No team size data yet.</p>
          ) : (
            <div className="space-y-3">
              {teamData.map((d, i) => (
                <DistributionBar key={d.key} label={d.label} count={d.count} total={teamTotal} color={COLORS[i % COLORS.length]} />
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-graphite-700/40 pt-5">
          <h4 className="text-xs font-semibold text-silver-300 uppercase tracking-wider mb-3">Work Mode</h4>
          {modeTotal === 0 ? (
            <p className="text-sm text-silver-400 py-4 text-center">No work mode data yet.</p>
          ) : (
            <div className="space-y-3">
              {modeData.map((d, i) => (
                <DistributionBar key={d.key} label={d.label} count={d.count} total={modeTotal} color={COLORS[(i + 3) % COLORS.length]} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}